<template>
  <e-layout-publico titulo="Pré-matrícula" :largura-maxima="900">
    <!-- v-show: o formulário fica montado durante a etapa do código, sem perder as escolhas -->
    <div v-show="etapa === 'formulario'">
      <v-form @submit.prevent="avancar">
        <!-- Um card só: a barra das abas é o cabeçalho colorido, e os campos ficam no corpo -->
        <v-card outlined class="mb-4">
          <!-- Modelo "icons-and-text" do Vuetify: ícone acima do texto, aba ativa clara -->
          <v-tabs
            v-model="aba"
            centered
            dark
            icons-and-text
            show-arrows
            background-color="primary"
            slider-color="white"
          >
            <v-tab href="#aba-turma">Turma<v-icon>fa fa-school</v-icon></v-tab>
            <v-tab href="#aba-aluno">Aluno<v-icon>fa fa-child</v-icon></v-tab>
            <v-tab href="#aba-pagador">Responsável<v-icon>fa fa-user</v-icon></v-tab>
          </v-tabs>

          <!-- eager: todas as abas ficam montadas, para validar e manter o que foi preenchido -->
          <v-tabs-items v-model="aba">
            <v-tab-item value="aba-turma" eager>
              <v-card-text>
                <ValidationObserver ref="aba-turma">
                  <e-escolha-de-turma :dados="dados" @erro="erroFormulario = $event" />
                </ValidationObserver>
              </v-card-text>
            </v-tab-item>
            <v-tab-item value="aba-aluno" eager>
              <v-card-text>
                <!-- CPF e nascimento vêm da tela de entrada: só exibidos, sem edição -->
                <v-row>
                  <v-col cols="12" md="6">
                    <e-label>CPF do aluno</e-label>
                    <v-text-field :value="dados.cpf" readonly disabled dense solo />
                  </v-col>
                  <v-col cols="12" md="6">
                    <e-label>Data de nascimento</e-label>
                    <v-text-field
                      :value="formatarData(dados.data_nascimento)"
                      readonly
                      disabled
                      dense
                      solo
                    />
                  </v-col>
                </v-row>
                <v-alert v-if="!completo" type="info" outlined dense>
                  Aluno já cadastrado: os dados vêm do cadastro da escola.
                </v-alert>
                <ValidationObserver ref="aba-aluno">
                  <e-dados-do-aluno
                    v-if="completo"
                    :dados="dados"
                    @erro="erroFormulario = $event"
                  />
                </ValidationObserver>
              </v-card-text>
            </v-tab-item>
            <v-tab-item value="aba-pagador" eager>
              <v-card-text>
                <p class="caption">Estes dados aparecem no boleto como pagador.</p>
                <ValidationObserver ref="aba-pagador">
                  <e-dados-do-pagador :dados="dados" />
                </ValidationObserver>
              </v-card-text>
            </v-tab-item>
          </v-tabs-items>
        </v-card>

        <v-alert v-if="erroFormulario" type="error" outlined role="alert">
          {{ erroFormulario }}
        </v-alert>

        <div class="d-flex mt-2">
          <v-btn v-if="indiceDaAba > 0" text @click="aba = abas[indiceDaAba - 1]">Anterior</v-btn>
          <v-btn v-else text :to="{ name: 'preMatricula.entrada' }">Voltar</v-btn>
          <v-spacer />
          <v-btn color="primary" type="submit" :loading="enviando" :disabled="enviando">
            {{ ehUltimaAba ? "Continuar" : "Próximo" }}
          </v-btn>
        </div>
      </v-form>
    </div>

    <e-secao-formulario
      v-if="etapa === 'codigo'"
      titulo="Confirmação por e-mail"
      icone="fa fa-envelope"
      class="mx-auto codigo"
    >
      <e-codigo-de-confirmacao
        :email="dados.responsavel_email"
        :enviado-em="codigo.enviadoEm"
        :validade-em-minutos="codigo.validadeEmMinutos"
        :enviando="enviando"
        :confirmando="confirmando"
        :erro="erroCodigo"
        @confirmar="confirmar"
        @reenviar="enviarCodigo"
        @voltar="etapa = 'formulario'"
      />
      <p v-if="erroCodigo" class="caption mt-3">
        Se a pré-matrícula deste aluno já foi feita, recupere o boleto informando CPF e data de
        nascimento na
        <router-link :to="{ name: 'preMatricula.entrada' }">tela de entrada</router-link>.
      </p>
    </e-secao-formulario>
  </e-layout-publico>
</template>

<script>
import ELayoutPublico from "@/components/PreMatriculas/ELayoutPublico.vue";
import ESecaoFormulario from "@/components/PreMatriculas/ESecaoFormulario.vue";
import EEscolhaDeTurma from "@/components/PreMatriculas/EEscolhaDeTurma.vue";
import EDadosDoAluno from "@/components/PreMatriculas/EDadosDoAluno.vue";
import EDadosDoPagador from "@/components/PreMatriculas/EDadosDoPagador.vue";
import ECodigoDeConfirmacao from "@/components/PreMatriculas/ECodigoDeConfirmacao.vue";
import { formatarData, mensagemDeErro } from "@/components/PreMatriculas/helpers";

// Coletados só no formulário completo (CPF sem cadastro).
const CAMPOS_DO_ALUNO = [
  "nome",
  "sexo_id",
  "filiacao_1",
  "filiacao_2",
  "cor_raca",
  "pais",
  "uf_id",
  "cidade_nascimento",
  "endereco_cep",
  "endereco_logradouro",
  "endereco_numero",
  "endereco_bairro",
  "endereco_cidade",
  "endereco_uf",
];

/**
 * Formulário completo (CPF sem cadastro) ou reduzido (aluno já cadastrado), seguido do código
 * enviado ao e-mail do responsável. Nada é gravado antes do código: a gravação leva o formulário
 * inteiro mais o código na mesma requisição.
 *
 * Os parâmetros vêm da tela de entrada e não sobrevivem a um recarregamento; sem eles, volta para
 * a entrada.
 */
export default {
  name: "PreMatriculaFormularioPage",
  components: {
    ELayoutPublico,
    ESecaoFormulario,
    EEscolhaDeTurma,
    EDadosDoAluno,
    EDadosDoPagador,
    ECodigoDeConfirmacao,
  },
  data() {
    const { cpf, dataNascimento, completo } = this.$route.params;
    return {
      completo: Boolean(completo),
      etapa: "formulario",
      aba: "aba-turma",
      enviando: false,
      confirmando: false,
      erroFormulario: null,
      erroCodigo: null,
      codigo: { email: null, enviadoEm: 0, validadeEmMinutos: 5 },
      dados: {
        cpf,
        data_nascimento: dataNascimento,
        turma_id: null,
        serie_id: null,
        nome: null,
        sexo_id: null,
        filiacao_1: null,
        filiacao_2: null,
        cor_raca: null,
        pais: null,
        uf_id: null,
        cidade_nascimento: null,
        endereco_cep: null,
        endereco_logradouro: null,
        endereco_numero: null,
        endereco_bairro: null,
        endereco_cidade: null,
        endereco_uf: null,
        responsavel_cpf: null,
        responsavel_nome: null,
        responsavel_email: null,
        responsavel_logradouro: null,
        responsavel_numero: null,
        responsavel_bairro: null,
        responsavel_cidade: null,
        responsavel_uf: null,
        responsavel_cep: null,
      },
    };
  },
  created() {
    if (!this.dados.cpf || !this.dados.data_nascimento) {
      this.$router.replace({ name: "preMatricula.entrada" });
    }
  },
  computed: {
    abas() {
      return ["aba-turma", "aba-aluno", "aba-pagador"];
    },
    indiceDaAba() {
      return this.abas.indexOf(this.aba);
    },
    ehUltimaAba() {
      return this.indiceDaAba === this.abas.length - 1;
    },
  },
  methods: {
    // Valida a aba atual antes de seguir; na última, revalida todas e para na primeira com erro.
    async avancar() {
      if (!(await this.$refs[this.aba].validate())) return;
      if (!this.ehUltimaAba) {
        this.aba = this.abas[this.indiceDaAba + 1];
        return;
      }
      const validas = await Promise.all(this.abas.map((aba) => this.$refs[aba].validate()));
      const primeiraInvalida = this.abas[validas.indexOf(false)];
      if (primeiraInvalida) {
        this.aba = primeiraInvalida;
        return;
      }
      this.continuar();
    },
    formatarData,
    // Aluno já cadastrado: os campos do aluno ficam fora do envio, só CPF e nascimento vão.
    dadosParaEnvio() {
      if (this.completo) return { ...this.dados };
      const dados = { ...this.dados };
      CAMPOS_DO_ALUNO.forEach((campo) => delete dados[campo]);
      return dados;
    },
    codigoAindaValido() {
      const validoAte = this.codigo.enviadoEm + this.codigo.validadeEmMinutos * 60 * 1000;
      return this.codigo.email === this.dados.responsavel_email && Date.now() < validoAte;
    },
    continuar() {
      this.erroFormulario = null;
      if (this.codigoAindaValido()) {
        this.etapa = "codigo";
        return;
      }
      this.enviarCodigo();
    },
    async enviarCodigo() {
      this.enviando = true;
      try {
        const pedido = {
          cpf: this.dados.cpf,
          responsavel_email: this.dados.responsavel_email,
          responsavel_nome: this.dados.responsavel_nome,
        };
        // No formulário reduzido o nome não é coletado, e a API aceita sem ele.
        if (this.completo) pedido.nome = this.dados.nome;
        const resposta = await this.$services.preMatriculasService.enviarCodigo(pedido);
        this.codigo = {
          email: this.dados.responsavel_email,
          enviadoEm: Date.now(),
          validadeEmMinutos: resposta.validade_em_minutos || 5,
        };
        this.erroCodigo = null;
        this.etapa = "codigo";
      } catch (error) {
        if (this.etapa === "codigo") this.erroCodigo = mensagemDeErro(error);
        else this.erroFormulario = mensagemDeErro(error);
      }
      this.enviando = false;
    },
    async confirmar(codigo) {
      this.confirmando = true;
      this.erroCodigo = null;
      try {
        const resposta = await this.$services.preMatriculasService.gravar({
          ...this.dadosParaEnvio(),
          codigo,
        });
        this.$router.replace({
          name: "preMatricula.boleto",
          params: { boleto: resposta.boleto, mensagem: resposta.message, recemCriada: true },
        });
      } catch (error) {
        this.erroCodigo = mensagemDeErro(error);
      }
      this.confirmando = false;
    },
  },
};
</script>

<style scoped>
.codigo {
  max-width: 560px;
}
</style>
